import {
  Box,
  Card,
  CardContent,
  TextField,
  Button,
  Typography,
  Dialog,
  DialogContent,
  CircularProgress,
  Snackbar,
  Alert
} from "@mui/material";
import { useState } from "react";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { API_BASE_URL, EMAIL_SUPPORT, PHONE_SUPPORT } from "../services/Constants";

export default function ContactSection() {

  const [form, setForm] = useState({
    firstName: "",
    lastName: "",
    phone: "",
    email: "",
    message: ""
  });

  const [successOpen, setSuccessOpen] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const [loading, setLoading] = useState(false);

  // ------------------------
  // Validation
  // ------------------------
  const validateForm = () => {
    if (!form.firstName.trim()) return "First name is required";
    if (!form.phone.trim()) return "Phone number is required";
    if (!/^[0-9]{10}$/.test(form.phone))
      return "Enter valid 10 digit phone number";
    if (!form.email.trim()) return "Email is required";
    if (!/\S+@\S+\.\S+/.test(form.email))
      return "Enter valid email address";
    if (!form.message.trim()) return "Message is required";
    return null;
  };

  // ------------------------
  // Submit Handler
  // ------------------------
  const handleSubmit = async () => {

    const error = validateForm();
    if (error) {
      setErrorMsg(error);
      return;
    }

    const payload = {
      first_name: form.firstName,
      last_name: form.lastName,
      phone: form.phone,
      email: form.email,
      message: form.message
    };

    try {
      setLoading(true);

      const response = await fetch(
        `${API_BASE_URL}/enquiry/addContact`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json"
          },
          body: JSON.stringify(payload)
        }
      );

      if (!response.ok) {
        throw new Error("Failed to submit message");
      }

      const data = await response.json();
      console.log("API Response:", data);

      setSuccessOpen(true);

      // Reset form
      setForm({
        firstName: "",
        lastName: "",
        phone: "",
        email: "",
        message: ""
      });

    } catch (error) {
      setErrorMsg("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Box
        px={{ xs: 2, sm: 4, md: 8 }}
        py={{ xs: 6, md: 10 }}
        sx={{ backgroundColor: "#f0f0f0" }}
      >
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
            gap: 6,
            alignItems: "center",
          }}
        >

          {/* LEFT CONTENT */}
          <Box>
            <Typography variant="h3" fontWeight={700} mb={2}>
              Get in <br /> Touch with us
            </Typography>

            <Typography color="text.secondary" mb={4}>
              We’re here to help! Reach out anytime.
            </Typography>

            <Typography fontWeight={700} mb={2}>
              📧 {EMAIL_SUPPORT}
            </Typography>

            <Typography fontWeight={700} mb={2}>
              📞 {PHONE_SUPPORT}
            </Typography>

            <Typography color="text.secondary">
              📍 Bapanakunta, ReddyPalle, Nallamada,
              Sri Sathya Sai, Andhra Pradesh
            </Typography>
          </Box>

          {/* RIGHT FORM */}
          <Card sx={{ borderRadius: 4, boxShadow: "0 10px 30px rgba(0,0,0,0.08)" }}>
            <CardContent sx={{ p: 4 }}>

              <Box display="grid" gridTemplateColumns={{ xs: "1fr", sm: "1fr 1fr" }} gap={2} mb={3}>
                <TextField
                  label="First Name"
                  size="small"
                  fullWidth
                  value={form.firstName}
                  onChange={(e) => setForm({ ...form, firstName: e.target.value })}
                />
                <TextField
                  label="Last Name"
                  size="small"
                  fullWidth
                  value={form.lastName}
                  onChange={(e) => setForm({ ...form, lastName: e.target.value })}
                />
              </Box>

              <Box mb={3}>
                <TextField
                  label="Phone Number"
                  size="small"
                  fullWidth
                  value={form.phone}
                  onChange={(e) => setForm({ ...form, phone: e.target.value })}
                />
              </Box>

              <Box mb={3}>
                <TextField
                  label="Email"
                  type="email"
                  size="small"
                  fullWidth
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                />
              </Box>

              <Box mb={4}>
                <TextField
                  label="How can we help you?"
                  multiline
                  rows={4}
                  fullWidth
                  value={form.message}
                  onChange={(e) => setForm({ ...form, message: e.target.value })}
                />
              </Box>

              <Box textAlign="right">
                <Button
                  variant="contained"
                  onClick={handleSubmit}
                  disabled={loading}
                  sx={{
                    backgroundColor: "#253A8F",
                    px: 4,
                    py: 1.2,
                    borderRadius: 2,
                    textTransform: "none",
                    fontWeight: 600,
                    "&:hover": { backgroundColor: "#1e2f73" },
                  }}
                >
                  {loading ? (
                    <CircularProgress size={22} color="inherit" />
                  ) : (
                    "Send Message"
                  )}
                </Button>
              </Box>

            </CardContent>
          </Card>
        </Box>
      </Box>

      {/* SUCCESS DIALOG */}
      <Dialog open={successOpen} onClose={() => setSuccessOpen(false)}>
        <DialogContent sx={{ textAlign: "center", py: 4 }}>
          <CheckCircleIcon sx={{ fontSize: 70, color: "green", mb: 2 }} />
          <Typography variant="h6" fontWeight={700}>
            Thank You for Contacting Us!
          </Typography>
          <Typography mt={1}>
            We have received your message. Our team will get back to you shortly.
          </Typography>

          <Button
            variant="contained"
            sx={{ mt: 3, borderRadius: 2 }}
            onClick={() => setSuccessOpen(false)}
          >
            OK
          </Button>
        </DialogContent>
      </Dialog>

      {/* ERROR SNACKBAR */}
      <Snackbar
        open={!!errorMsg}
        autoHideDuration={4000}
        onClose={() => setErrorMsg("")}
      >
        <Alert severity="error" variant="filled">
          {errorMsg}
        </Alert>
      </Snackbar>
    </>
  );
}
